import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ModalService } from '@purespectrum1/ui/modal';

import {
  AdvanceTargetingQualification,
  CombinedQualification,
  Flexible,
  Qualification,
  UpdateAdvanceTargetingCoreQualification,
} from '../../shared/interfaces/qualification.interface';
import { SelectedQuota } from '../../shared/interfaces/quota.interface';
import { AdvanceTargetingModalComponent } from '../advance-targeting-qualification/advance-targeting-modal/advance-targeting-modal.component';
import { notifyMessage } from '../../shared/constants/notify-message';
import { ConfirmationModalComponent } from '../../shared/ui/confirmation-modal/confirmation-modal.component';
import { Constants } from '../../shared/constants/create-survey.constants';
import { map } from 'rxjs/operators';
import { QuotaModalService } from '../quota-modal/quota-modal.service';
import { Subscription } from 'rxjs';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { AidaPayloadService } from '../../shared/services/aida/aida-payload/aida-payload.service';
import { AidaPayload } from '../../shared/interfaces/aida.interface';
import { AidaSurveyQuotasEditInspector } from '../../shared/domain/aida-survey-quotas-edit-inspector';

@Component({
  selector: 'ps-ui-advance-targeting-qualification',
  templateUrl: './advance-targeting-qualification.component.html',
  styleUrls: ['./advance-targeting-qualification.component.css'],
})
export class AdvanceTargetingQualificationComponent
  implements AfterViewChecked, OnInit, OnDestroy
{
  @Input() advanceTargetingButtons: number[] = [1, 2, 3];
  @Input() complete!: number;
  @Input() clickBalance!: number;
  @Input() advanceTargetingQualifications: AdvanceTargetingQualification[] = [];
  @Input() qualifications: Qualification[] = [];
  @Input() hasQuotas: (qualification: Qualification) => boolean = () => false;
  @Input() locale: string = '';
  @Input() isQlcLiveSurvey: boolean = false;
  @Input() showAllQualifications: boolean = false;
  @Input() changeCompletes: EventEmitter<number> = new EventEmitter<number>();
  @Input() hasNestedGroup: boolean = false;
  @Input() isLiveSurveyEdit: boolean = false;
  @Input() alwaysShowQualCodes: number[];
  @Input() isLiveEditSurvey = false;

  @Output() qualificationAdded = new EventEmitter<
    AdvanceTargetingQualification[]
  >();
  @Output() readonly quotaChange = new EventEmitter();
  @Output() setCoreQualificationIfDependent =
    new EventEmitter<UpdateAdvanceTargetingCoreQualification>();
  readonly QUALIFICATIONS_RENDER_TYPE = Constants.QUALIFICATIONS_RENDER_TYPE;
  completesSubscription?: Subscription;
  private _flexible?: Flexible;
  private _aida: AidaPayload = { enabled: false, selected_quota_ids: [] };
  private _subscriptions: Subscription = new Subscription();

  constructor(
    private _modal: ModalService,
    private _toastr: ToasterService,
    private _cd: ChangeDetectorRef,
    private _quotaModalService: QuotaModalService,
    private _aidaPayloadService: AidaPayloadService
  ) {}

  ngOnInit() {
    this._handleAidaPayload();
    this.completesSubscription = this.changeCompletes.subscribe(
      (newCompletes: number) => {
        // Update existing AT quotas
        this.advanceTargetingQualifications.forEach((qualification) => {
          const quotas = qualification.quotas;
          this._flexible = qualification.flexible;
          if (!quotas) {
            return;
          }
          const updatedQuotas =
            this._quotaModalService.reDistributeQuotaQuantites(
              quotas,
              newCompletes,
              this._flexible
            );

          // CreateSurveys.setQuota expects the data and render_type properties to be set to correctly function
          const updatedQualification: Partial<CombinedQualification> = {
            data: qualification.selectedAnswers,
            render_type:
              Constants.QUALIFICATIONS_RENDER_TYPE.MULTIPUNCH_DROPDOWN,
            ...qualification,
          };

          this.updateQuotas({
            msg: Constants.QUOTA_MODAL_ACTION.APPLY_QUOTA,
            qualification: updatedQualification,
            quotas: updatedQuotas,
            flexible: this._flexible,
          });
        });
      }
    );
  }

  ngAfterViewChecked(): void {
    this._updateDisableAdvanceTargetingButtonCount();
    this._cd.detectChanges();
  }

  ngOnDestroy() {
    this.completesSubscription?.unsubscribe();
    this._subscriptions.unsubscribe();
  }

  private _handleAidaPayload() {
    const _aidaPayloadSubscription = this._aidaPayloadService
      .getAidaLiveEditPayload()
      .subscribe((payload) => {
        this._aida = payload;
      });

    this._subscriptions.add(_aidaPayloadSubscription);
  }

  private _showQuotaEditNotAllowedErrorForAida(
    quotas: SelectedQuota[] = [],
    qualificationHasNestedQuotas = false
  ): boolean {
    if (!this.isLiveEditSurvey) {
      return false;
    }
    return new AidaSurveyQuotasEditInspector(
      this._aida,
      quotas,
      qualificationHasNestedQuotas
    ).notEditable();
  }

  openAdvanceTargetingModal(question?: AdvanceTargetingQualification) {
    const modalRef = this._modal.open(AdvanceTargetingModalComponent, {
      data: {
        question,
        advanceTargetingQualifications: this.advanceTargetingQualifications,
        locale: this.locale,
        isQlcLiveSurvey: this.isQlcLiveSurvey,
        alwaysShowQualCodes: this.alwaysShowQualCodes,
      },
      width: '600px',
    });

    modalRef.onClose$.subscribe(async (response) => {
      if (response?.msg === 'applyTargeting') {
        const shouldShowEditQuotaNotAllowedError =
          this._showQuotaEditNotAllowedErrorForAida(
            question?.quotas,
            question?.hasNestedQuotas
          );
        if (shouldShowEditQuotaNotAllowedError) {
          this._toastr.error(
            notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
          );
          return;
        }
        const isParentUnsupported = this._validateIfParentIsUnsupported(
          response.question
        );
        if (isParentUnsupported) {
          return this._toastr.warning(
            notifyMessage.warningMessage.UNSUPPORTED_PARENT_QUALIFICATION
          );
        }
        const isValid = await this._validateCoreQualificationDependency({
          action: Constants.ADVANCE_TARGETING_ACTION.ADD,
          question: response.question,
          parentQuestion: response.parent,
        });
        if (isValid) {
          this._toastr.success(notifyMessage.successMessage.ADV_QUESTION_ADDED);
          this._updateadvanceTargetingQualifications(
            !!question,
            response.question,
            response.parent
          );
          this._updateDisableAdvanceTargetingButtonCount();
          this.qualificationAdded.emit(this.advanceTargetingQualifications);
        }
      }
    });
  }

  async deleteAdvanceTargetingQuestion(index: number) {
    const question = this.advanceTargetingQualifications[index];
    if (this.isQlcLiveSurvey) {
      return;
    }
    if (
      this._showQuotaEditNotAllowedErrorForAida(
        question.quotas,
        question.hasNestedQuotas
      )
    ) {
      this._toastr.error(
        notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
      );
      return;
    }
    const isValid = await this._validateCoreQualificationDependency({
      action: Constants.ADVANCE_TARGETING_ACTION.DELETE,
      question,
    });
    if (!isValid) {
      return;
    }

    /**Get index of current and all child Qualifications */
    const deleteIndexes = this._getDeletingIndexes(index, question);

    /**Remove child QualId from parent on deleting child Qualifications */
    this._updateParentOnChildDelete(question);

    deleteIndexes.forEach((deleteIndex) => {
      this.setCoreQualificationIfDependent.emit({
        action: Constants.ADVANCE_TARGETING_ACTION.DELETE,
        question: this.advanceTargetingQualifications[deleteIndex],
      });
      this.advanceTargetingQualifications.splice(deleteIndex, 1);
    });
    this.qualificationAdded.emit(this.advanceTargetingQualifications);
    this._updateDisableAdvanceTargetingButtonCount();
  }

  private _getDeletingIndexes(
    index: number,
    question: AdvanceTargetingQualification
  ) {
    const deleteIndexes = [index];

    if (question.childs?.length) {
      question.childs.forEach((childQualId) => {
        const childIndex = this.advanceTargetingQualifications.findIndex(
          (existingQuestion) =>
            existingQuestion.qualification_code === childQualId
        );

        if (childIndex !== -1) {
          deleteIndexes.push(childIndex);
        }
      });
    }

    return deleteIndexes;
  }

  private _updateParentOnChildDelete(question: AdvanceTargetingQualification) {
    if (question.hasDependency && question.dependency?.parentQualId) {
      const parentIndex = this.advanceTargetingQualifications.findIndex(
        (existingQuestion) =>
          existingQuestion.qualification_code ===
          question.dependency?.parentQualId
      );

      if (parentIndex !== -1) {
        this.advanceTargetingQualifications[parentIndex].childs =
          this.advanceTargetingQualifications[parentIndex]?.childs?.filter(
            (item) => item !== question.qualification_code
          );
      }
    }
  }

  private _updateadvanceTargetingQualifications(
    update: boolean,
    question: AdvanceTargetingQualification,
    parentQuestion: AdvanceTargetingQualification | undefined
  ) {
    if (update) {
      const index = this.advanceTargetingQualifications.findIndex(
        (existingQuestion) =>
          existingQuestion.qualification_code === question.qualification_code
      );
      if (index !== -1) {
        this.advanceTargetingQualifications[index] = question;
      } else {
        this.advanceTargetingQualifications.push(question);
      }
    } else {
      this.advanceTargetingQualifications.push(question);
      if (parentQuestion) {
        this.advanceTargetingQualifications.push(parentQuestion);
      }
    }
    this.setCoreQualificationIfDependent.emit({
      action: Constants.ADVANCE_TARGETING_ACTION.ADD,
      question: parentQuestion || question,
    });
  }

  private _updateDisableAdvanceTargetingButtonCount() {
    this.advanceTargetingButtons = [];
    if (this.advanceTargetingQualifications.length > 3) {
      return;
    }
    for (
      let index = 1;
      index < 4 - this.advanceTargetingQualifications.length;
      index++
    ) {
      this.advanceTargetingButtons.push(index);
    }
  }

  updateQuotas(response: {
    msg: string;
    qualification: Partial<CombinedQualification>;
    quotas: SelectedQuota[];
    flexible?: Flexible;
  }) {
    // attach quotas to advance targeting question on apply quotas, attached quotas are used in redistribution
    this.advanceTargetingQualifications.forEach(
      (advanceTargetingQualification) => {
        if (
          advanceTargetingQualification.qualification_code ===
          response.qualification.qualification_code
        ) {
          advanceTargetingQualification.quotas = response.quotas;
        }
      }
    );

    this.quotaChange.emit(response);
  }

  private async _validateCoreQualificationDependency(
    payload: UpdateAdvanceTargetingCoreQualification
  ) {
    // check dependency on `parentQuestion` since `question` already has dependency on `parentQuestion`
    const advanceTargetingQuestion = payload.parentQuestion || payload.question;
    const update = !!this.advanceTargetingQualifications.find(
      (qualification) =>
        advanceTargetingQuestion.qualification_code ===
        qualification.qualification_code
    );
    const parentQualificationIndex = this.qualifications?.findIndex(
      (coreQualification) =>
        advanceTargetingQuestion &&
        advanceTargetingQuestion.hasDependency &&
        advanceTargetingQuestion.dependency &&
        coreQualification.qualification_code ===
          advanceTargetingQuestion.dependency.parentQualId
    );
    if (parentQualificationIndex !== -1) {
      const parentQualification = this.qualifications[parentQualificationIndex];
      if (payload.action === Constants.ADVANCE_TARGETING_ACTION.ADD) {
        // check if parentQualification is already added as a dependent of another AT question
        if (!update && parentQualification.isAdvanceTargetingParent) {
          await this._openConfirmationModal(
            Constants.CONFIRM_MODAL_TEXT.ADVANCE_CORE_DEPENDENCY_EXISTS
          );
          this.openAdvanceTargetingModal(payload.question);
          return false;
        }

        return this._checkIfCoreQualificationOrQuotaExists(
          payload,
          parentQualification,
          update
        );
      } else if (payload.action === Constants.ADVANCE_TARGETING_ACTION.DELETE) {
        // check if AT question has core qualification as parent
        if (parentQualification.isAdvanceTargetingParent) {
          return this._openConfirmationModal(
            Constants.CONFIRM_MODAL_TEXT.DELETE_ADVANCE_CORE_DEPENDENCY.replace(
              '<QUALIFICATION_NAME>',
              `${parentQualification.qualification_name}`
            )
          );
        }
      }
    }
    return true;
  }

  private async _checkIfCoreQualificationOrQuotaExists(
    payload: UpdateAdvanceTargetingCoreQualification,
    parentQualification: Qualification,
    update: boolean
  ) {
    // check if any option is deselected on parent qualification
    const hasQualification = this._hasQualification(parentQualification);

    // check if quota exists on parent qualification
    const hasQuota = this.hasQuotas(parentQualification);
    if (!update && (hasQualification || hasQuota)) {
      const success = await this._openConfirmationModal(
        Constants.CONFIRM_MODAL_TEXT.ADVANCE_CORE_DEPENDENCY.replace(
          '<QUALIFICATION_NAME>',
          `${parentQualification.qualification_name}`
        ),
        'Continue'
      );
      if (success) {
        return true;
      }
      this.openAdvanceTargetingModal(payload.question);
      return false;
    }
    return true;
  }

  private _validateIfParentIsUnsupported(
    childQualification: AdvanceTargetingQualification
  ) {
    return Constants.UNSUPPORTED_QUALIFICATIONS.some(
      (unsupportedQual) =>
        childQualification &&
        childQualification.hasDependency &&
        childQualification.dependency &&
        childQualification.dependency.parentQualId === unsupportedQual
    );
  }

  private _hasQualification(qualification: Qualification) {
    if (qualification.is_location) {
      // check if any other location has qualification
      const locationQualifications = this.qualifications.filter(
        (singleQualification) => singleQualification.is_location
      );
      let hasQualification = false;
      for (const locationQualification of locationQualifications) {
        const checkBasedOnRenderType: { [key: string]: () => boolean } = {
          [this.QUALIFICATIONS_RENDER_TYPE.SEARCH]: () =>
            !!locationQualification.data.length,
          [this.QUALIFICATIONS_RENDER_TYPE.MULTIPUNCH_LIST]: () =>
            !locationQualification.data.every(
              (singleOption) => !singleOption.selected
            ),
        };
        hasQualification =
          checkBasedOnRenderType[locationQualification.render_type] &&
          checkBasedOnRenderType[locationQualification.render_type]();
        if (hasQualification) {
          break;
        }
      }
      return hasQualification;
    }

    return !qualification.data.every((singleOption) => singleOption.selected);
  }

  private _openConfirmationModal(text: string, confirmButtonLabel?: string) {
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text,
        confirmButtonLabel,
      },
    });
    return modalRef.onClose$.pipe(map((msg) => msg === 'ok')).toPromise();
  }
}
